import {Component, SimpleChanges} from '@angular/core';
import {Utils} from '../../common/utils';
import {TreeComponent, TreeNode, TreeFlatNode} from '../../common/tree/tree.component';
import {Observable} from 'rxjs';
import {DynamicImageSet} from 'src/app/model/dynamic-image-set';
import {ErrorHelper} from '../../common/ErrorHelper';

@Component({
  selector: 'app-features-tree-component',
  templateUrl: '../../common/tree/tree.component.html',
  styleUrls: ['../../common/tree/tree.component.scss'],
})
export class FeaturesTreeComponent extends TreeComponent {

  mandatorySelectedFamilies = ['0M', '0P'];
  standardOptions = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.id && changes.id.currentValue !== changes.id.previousValue
      || changes.version && changes.version.currentValue !== changes.version.previousValue) {
        this.initialize();
        this.loadDataAsync();
    } else {
      this.images = [];
      this.angles = this.dynamicImageSet.previewAngles;
      this.loadOnlyImagesAsync();
    }
  }

  loadPreselectedNodes(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      resolve(true);
    });
  }

  loadTreeData(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.selectedNodes = this.checklistSelection.selected
        .filter(node => !!node)
        .filter(node => node.item !== 'none')
        .map(node => node.item);
      this.checklistSelection.clear();
      return this.apiService.getFeatures(this.dynamicImageSet, this.selectedNodes, this.testArea).toPromise().then((data) => {
        console.log('Fetched all the features');
        const groupedData = Utils.groupBy(data, ['categoryName', 'featureClass']);
        this.dataSource.data = this.buildTree(groupedData, 0);
        console.log('Features tree built');
        this.loadStandardOptions();
        resolve(true);
      }, (error) => {
        const errorMessage = ErrorHelper.disectError('fetch features', error);
        this.notifierService.notify('error', errorMessage);
        reject('Could not load features');
      });
    });
  }

  buildTree(obj: { [key: string]: any }, level: number): TreeNode[] {
    return Object.keys(obj).reduce<TreeNode[]>((accumulator, key) => {
      const value = obj[key];
      const node = new TreeNode();
      node.item = key;

      if (value != null) {
        if (typeof value === 'object' && level < 2) {
          node.children = this.buildTree(value, level + 1);
          if (level === 1 && !this.mandatorySelectedFamilies.includes(key)) {
            const noneNode = new TreeNode();
            noneNode.item = 'none';
            node.children.push(noneNode);
          }
        } else {
          node.item = value.featureId;
          node.description = value.featureLabel;
          node.isSelected = value.isSelected;
        }
      }

      return accumulator.concat(node);
    }, []);
  }

  layerSelectionToggle(node: TreeFlatNode): void {
    const siblings = this.treeControl.getDescendants(this.getParentNode(node));
    for (const sibling of siblings) {
      this.checklistSelection.deselect(sibling);
    }
    this.checklistSelection.select(node);
    this.reloadAsync();
  }

  async reloadAsync(): Promise<any> {
    try {
      await this.reloadFeatures();
      await this.loadStandardOptions();
      await this.loadImages();
    } catch (error) {
      const errorMessage = ErrorHelper.disectError('reload features', error);
      this.notifierService.notify('error', errorMessage);
    }
  }

  reloadFeatures(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.selectedNodes = this.checklistSelection.selected
        .filter(n => !!n)
        .filter(n => n.item !== 'none')
        .map(n => n.item);
      return this.apiService.getFeatures(this.dynamicImageSet, this.selectedNodes, this.testArea).toPromise()
        .then((data) => {
          let oldNodes: TreeNode[] = Array.from(this.nestedNodeMap.keys());
          oldNodes = oldNodes.filter(node => node.item !== 'none');
          const newNodes = Object.assign([], data);

          /** mark nodes as disabled if necessary */
          oldNodes.forEach(oldNode => {
            const nodeExists: boolean = newNodes.some(newNode => newNode.featureId === oldNode.item);
            if (!nodeExists) {
              const theNode = this.nestedNodeMap.get(oldNode);
              if (theNode.level === 2) {
                theNode.isDisabled = true;
                theNode.isSelected = false;
              }
            }
          });


          newNodes.forEach(element => {
            const oldNode: TreeNode = oldNodes.find(n => n.item === element.featureId);
            if (oldNode === undefined) {
              /** add node if does not exist in the loaded configuration */
              const parentNode = oldNodes.find(n => n.item === element.featureClass);
              if (parentNode !== undefined) {
                const newNode = new TreeNode();
                newNode.item = element.featureId;
                newNode.description = element.featureLabel;
                newNode.isSelected = element.isSelected;
                newNode.isDisabled = false;
                parentNode.children.push(newNode);
              }
            } else {
              /** reenable disabled nodes if necessary */
              const theNode = this.nestedNodeMap.get(oldNode);
              if (theNode.isDisabled === true && theNode.level === 2) {
                theNode.isDisabled = false;
                theNode.isSelected = element.isSelected;
              }
            }
          });
          console.log('Refetched all the features');
          resolve(true);
        })
        .catch(error => {
          const errorMessage = ErrorHelper.disectError('get features', error);
          this.notifierService.notify('error', errorMessage);
          resolve(false);
        });
    });
  }

  getImage(dynamicImageSet: DynamicImageSet, selectedNodes: string[], view: string): Observable<Blob> {
    return this.apiService.getImageBasedOnOptions(dynamicImageSet, selectedNodes, view, this.testArea);
  }

  loadStandardOptions(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      return this.apiService.getStandardOptions(this.dynamicImageSet, this.selectedNodes, this.testArea).toPromise().then((data) => {
        this.standardOptions = data;
        console.log('Fetched all the standard options');
        this.showStandardFeaturesBehindNone();
        resolve(true);
    }, (error) => {
        const errorMessage = ErrorHelper.disectError('fetch standard options', error);
        this.notifierService.notify('error', errorMessage);
        reject('Could not load standard options');
    });
    });
  }

  showStandardFeaturesBehindNone(): Promise<boolean> {
    let parentNodes: TreeFlatNode[] = Array.from(this.nestedNodeMap.values());
    parentNodes = parentNodes.filter(layerFlatNode => layerFlatNode.level === 1);
    parentNodes.forEach(parentNode => {
      const children = this.treeControl.getDescendants(parentNode);
      if (Array.isArray(children) && children.length > 0) {
        const standardOption = this.standardOptions.find(code => code.featureClass === parentNode.item);
        const noneNode = children.filter(n => !!n).find(node => node.item === 'none');
        if (standardOption !== undefined && noneNode !== undefined) {
          noneNode.description = '(' + standardOption.featureId + ')' + ' ' + standardOption.featureLabel;
          noneNode.isNone = true;
        }
      }
    });
    console.log('Standard options added behind none');
    return Promise.resolve(true);
  }

}
